<script lang="ts">
	import SectionProductSlider from './../../sections/ecom/SectionProductSlider.svelte';
	import { page } from '$app/stores';
	import Media from '$lib/components/media/Media.svelte';
	import Link from '$lib/components/navigation/Link.svelte';
	import GlobalMedia from '$lib/components/media/GlobalMedia.svelte';
	import Button from '$lib/components/util/Button.svelte';
	import Text from '$lib/components/Text.svelte';
	import { createEventDispatcher } from 'svelte';
	import { getText } from '$lib/util/translations';
	import api from '$lib/api/ecom/shopify';

	const dispatch = createEventDispatcher();

	export let searchOpen = true;
	export let headerCollapsed = false;

	let loading: boolean = false;
	let maxProductCount: number = 6;
	let searchField: any = null;
	let searchValue: string;
	let changeTimer: any = null;
	let products: any[] = [];

	$: onChange(searchValue);

	function onChange(...args: any[]) {
		loading = true;
		clearTimeout(changeTimer);

		if (searchValue?.length > 2) {
			changeTimer = setTimeout(() => {
				predictiveSearch();
			}, 500);
		} else {
			products = [];
		}
	}

	async function predictiveSearch() {
		const results = await api.prediciveSearch(searchValue, $page.params.lang, $page.data.location);

		const searchProducts = results?.predictiveSearch?.products.slice(0, maxProductCount) ?? [];

		products = searchProducts.filter((p: any) => p.product_type?.value === $page.data.customerType);

		loading = false;
	}

	$: if (searchOpen) {
		searchField.focus();
	}
</script>

<div class="search" class:open={searchOpen} class:collapsed={headerCollapsed}>
	<div class="inner-search">
		<div class="input-wrapper">
			<form
				on:submit|preventDefault={() => {
					search();
				}}
			>
				<input
					type="text"
					bind:this={searchField}
					bind:value={searchValue}
					placeholder={getText($page.data.globalTexts, 'search_placeholder').text}
				/>
			</form>
			<div class="search-icon">
				<GlobalMedia key="icon-search2" />
			</div>
			<div
				class="close-icon"
				on:click={() => {
					searchValue = '';
					dispatch('closeSearch', {});
				}}
			>
				<GlobalMedia key="icon-close" />
			</div>
		</div>
		{#if searchValue?.length > 0 && !loading}
			<div class="row">
				{#if products?.length > 0}
					<div class="results">
						{#each products as product, idx}
							{#key product}
								<div
									class="product fade-left started delay-{idx * 100}"
									on:click={() => {
										searchValue = '';
										dispatch('closeSearch', {});
									}}
								>
									<Link type="internal" link={'shop/' + product.handle}>
										<div class="img-container">
											<img src={product?.featuredImage?.url} alt={product?.title ?? ''} />
										</div>
										<div class="title">{product?.title ?? ''}</div>
									</Link>
								</div>
							{/key}
						{/each}
					</div>
				{:else}
					<div class="no-products">
						<Text key="ecom_no_products" />
					</div>
				{/if}
			</div>
			<div
				class="all-button"
				on:click={() => {
					searchValue = '';
					dispatch('closeSearch', {});
				}}
			>
				<Link type="internal" link={'shop?q=' + searchValue}>
					<Button
						size="medium"
						type="dark"
						textKey="ecom_show_all_results"
						iconKey="icon-arrow-right"
					/>
				</Link>
			</div>
		{/if}
	</div>
</div>

<style lang="scss">
	.search {
		position: fixed;
		top: calc(v(header__nav__height) + v(header__banner__height));
		width: 100%;
		background: color(white);
		display: flex;
		justify-content: center;
		padding: 0 var(--page-padding);
		opacity: 0;
		pointer-events: none;
		transition: all 0.3s ease;
		z-index: 4;

		@include media(phone) {
			padding: 0 var(--page-padding);
			display: none;
		}

		&.open {
			opacity: 1;
			pointer-events: auto;
		}

		&.collapsed {
			transform: translateY(-100%);
			top: 0;
		}

		.inner-search {
			width: 100%;
			max-width: var(--max-width);
			padding: v(content-padding) 0;

			form {
				width: 100%;
			}

			input {
				width: 100%;
				padding: 16px 48px;
				border: 0;
				background: color(grey_02);
				@include font(h6);
			}

			.input-wrapper {
				position: relative;
				display: flex;
				align-items: center;
				gap: v(content-padding);
			}

			.search-icon {
				position: absolute;
				top: 13px;
				inset-inline-start: 12px;
				width: 24px;
				cursor: pointer;
			}

			.close-icon {
				position: absolute;
				top: 13px;
				inset-inline-end: 12px;
				width: 24px;
				cursor: pointer;
			}
		}

		.results {
			margin-top: v(content-padding);
			margin-bottom: v(content-padding);
			display: flex;
			flex-wrap: wrap;
			gap: v(content-padding-sm);

			.product {
				width: 140px;

				:global(a) {
					display: flex;
					flex-direction: column;
					color: color(black);
				}

				.img-container {
					overflow: hidden;
					width: 140px;
					height: 140px;
					margin-bottom: 8px;
					background: color(grey_01);
				}

				img {
					width: 100%;
					height: 100%;
					object-fit: cover;
					transition: all 0.3s ease;
				}

				.title {
					@include font(text-sm);
				}

				&:hover {
					img {
						transform: scale(1.05);
					}
				}
			}
		}

		.no-products {
			margin: v(content-padding) 0;
			@include font(h6);
		}

		.all-button {
			display: inline-block;
		}
	}
</style>
